import { useCallback, useEffect, useState } from 'react';
import Page from '../../../../components/page/Page';
import Header from '../../../../components/administration/masters/attributes/Header';
import { Box, Button, IconButton, Paper, Tab, Tabs } from '@mui/material';
import useThemeMediaQuery from '../../../../../core/hooks/useThemeMediaQuery';
import { useNavigationCore } from '../../../../../core/router/navigation';
import { useMeCore } from '../../../../../core/modules/me';
import { useGlobalCore } from '../../../../../core/modules/global';
import CreateDialog from '../../../../components/administration/masters/attributes/CreateDialog';
import UpdateDialog from '../../../../components/administration/masters/attributes/UpdateDialog';
import { tAttribute, useMasterCore } from '../../../../../core/modules/master';
import { useCompanyCore } from '../../../../../core/modules/company';
import EditIcon from '@mui/icons-material/Edit';
import { CREATE_PRODUCT_ATTRIBUTE_DIALOG, UPDATE_PRODUCT_ATTRIBUTE_DIALOG } from '../../../../../core/constants/dialog';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import { ProductAttribute } from '../../../../../core/interfaces/productAttribute.interface';
import { DndContext, closestCenter } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DragHandleIcon from '@mui/icons-material/DragHandle';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

const Attributes = () => {
  const { user } = useMeCore();
  const { openDialog } = useGlobalCore();
  const { companyId } = useCompanyCore();
  const {
    productUsages,
    productCompletenesses,
    productFuntionalities,
    productPackagings,
    productStates,
    productAesthetics,
    setProductAttribute,
    getProductAttributes,
    putProductAttibute,
    clearError,
    updateAttributesOrder,
  } = useMasterCore();
  const { goToWelcome } = useNavigationCore();

  const isMobile = useThemeMediaQuery((theme) => theme.breakpoints.down('lg'))
  const [tab, setTab] = useState(0);
  const [cardinality, setCandinality] = useState(0);
  const [tAttribute, setTAttribute] = useState<tAttribute>('usage');

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTab(newValue);
  };

  useEffect(() => {
    if (
      !user ||
      (
        !user.privileges.superAdmin &&
        !user.privileges.writeMaster
      )
    ) {
      goToWelcome();
    }
  }, [user, goToWelcome]);

  useEffect(() => {
    if (companyId) {
      getProductAttributes(companyId, 'usage');
      getProductAttributes(companyId, 'packaging');
      getProductAttributes(companyId, 'functionality');
      getProductAttributes(companyId, 'aesthetic');
      getProductAttributes(companyId, 'completeness');
      getProductAttributes(companyId, 'state');
    }
  }, [companyId, getProductAttributes]);

  const selectType = useCallback((attribute: ProductAttribute) => {
    // Validar que el atributo tenga un ID
    if (!attribute || !attribute.id) {
      console.error('❌ Error: El atributo no tiene ID válido:', attribute);
      return;
    }

    let attributeType: tAttribute = 'usage';
    if (tab === 0) {
      attributeType = 'usage';
    } else if (tab === 1) {
      attributeType = 'packaging';
    } else if (tab === 2) {
      attributeType = 'functionality';
    } else if (tab === 3) {
      attributeType = 'aesthetic';
    } else if (tab === 4) {
      attributeType = 'completeness';
    } else {
      attributeType = 'state';
    }

    clearError();
    // Establecer el tipo primero
    setTAttribute(attributeType);
    // Luego establecer el atributo (esto dispara el useEffect en UpdateDialog)
    setProductAttribute(attribute);
    // Finalmente abrir el diálogo
    // Usar setTimeout para asegurar que el estado se actualice antes de abrir el diálogo
    setTimeout(() => {
      openDialog(UPDATE_PRODUCT_ATTRIBUTE_DIALOG);
    }, 0);
  }, [clearError, openDialog, setProductAttribute, tab]);

  const openCreateDialog = useCallback(() => {
    if (tab === 0) {
      setTAttribute('usage');
      setCandinality(productUsages.length);
    } else if (tab === 1) {
      setTAttribute('packaging');
      setCandinality(productPackagings.length);
    } else if (tab === 2) {
      setTAttribute('functionality');
      setCandinality(productFuntionalities.length);
    } else if (tab === 3) {
      setTAttribute('aesthetic');
      setCandinality(productAesthetics.length);
    } else if (tab === 4) {
      setTAttribute('completeness');
      setCandinality(productCompletenesses.length);
    } else {
      setTAttribute('state');
      setCandinality(productStates.length);
    }

    clearError();
    openDialog(CREATE_PRODUCT_ATTRIBUTE_DIALOG);
  }, [clearError, openDialog, productAesthetics.length, productCompletenesses.length, productFuntionalities.length, productPackagings.length, productStates.length, productUsages.length, tab]);

  function SortableItem({ data }: { data: ProductAttribute }) {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: data.id });
  
    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      padding: '8px 12px',
      margin: '4px 0',
      backgroundColor: isDragging ? '#e0f7fa' : '#fff',
      border: '1px solid #ccc',
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '8px',
    };
  
    const dragButtonStyle = {
      background: 'transparent',
      border: 'none',
      cursor: 'grab',
      padding: '4px',
    };
  
    return (
      <div ref={setNodeRef} style={style}>
        <button style={dragButtonStyle} {...attributes} {...listeners}>
          <DragHandleIcon />
        </button>
  
        <div style={{ flex: 1 }}>{data.name}</div>
  
        <IconButton aria-label="edit" onClick={() => selectType(data)}>
          <EditIcon />
        </IconButton>
      </div>
    );
  }

  const handleDragEnd = async (event: any, items: ProductAttribute[], attributeType: tAttribute) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = items.findIndex((item) => item.id === active.id);
    const newIndex = items.findIndex((item) => item.id === over.id);

    const newItems = arrayMove(items, oldIndex, newIndex);

    const [start, end] = oldIndex < newIndex ? [oldIndex, newIndex] : [newIndex, oldIndex];

    // Actualizar cardinalidades en los items reordenados
    const updatedItems = newItems.map((item, index) => ({
      ...item,
      cardinality: index,
    }));

    // Optimistic update: actualizar el estado local inmediatamente para respuesta instantánea
    updateAttributesOrder(attributeType, updatedItems);

    if (companyId) {
      // Hacer las llamadas PUT en paralelo en segundo plano
      // No recargamos después porque ya hicimos optimistic update
      Promise.all(
        updatedItems.slice(start, end + 1).map(item =>
          putProductAttibute(companyId, item.id, { ...item, cardinality: item.cardinality }, attributeType)
        )
      ).catch((error) => {
        // Solo recargar si hay error para revertir el estado
        console.error('Error updating attributes order:', error);
        getProductAttributes(companyId, attributeType);
      });
    }
  };

  return (
    <>
      <Page
        header={<Header />}
        content={
          <div className="w-full my-16 px-24 md:px-32">
            <Paper className="flex flex-col flex-auto items-center shadow rounded-2xl overflow-hidden">
              <div className="w-full p-32">
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }} className="flex flex-col md:flex-row items-center justify-between gap-8">
                  <Tabs
                    value={tab}
                    onChange={handleChange}
                    variant="scrollable"
                    scrollButtons="auto"
                  >
                    <Tab label="Uso" {...a11yProps(0)} />
                    <Tab label="Embalaje" {...a11yProps(1)} />
                    <Tab label="Funcionalidad" {...a11yProps(2)} />
                    <Tab label="Estético" {...a11yProps(3)} />
                    <Tab label="Completitud" {...a11yProps(4)} />
                    <Tab label="Estados General" {...a11yProps(5)} />
                  </Tabs>
                  <Button
                    variant="contained"
                    color="secondary"
                    className="mr-8 mb-8"
                    disabled={!companyId}
                    onClick={openCreateDialog}
                  >
                    <AddCircleOutlineOutlinedIcon />
                    <span className="mx-8">Crear</span>
                  </Button>
                </Box>
                <CustomTabPanel value={tab} index={0}>
                  <DndContext collisionDetection={closestCenter} onDragEnd={(ev) => handleDragEnd(ev, productUsages, 'usage')}>
                    <SortableContext items={productUsages} strategy={verticalListSortingStrategy}>
                      {productUsages.map((attr: ProductAttribute) => (
                        <SortableItem key={attr.id} data={attr} />
                      ))}
                    </SortableContext>
                  </DndContext>
                </CustomTabPanel>
                <CustomTabPanel value={tab} index={1}>
                  <DndContext collisionDetection={closestCenter} onDragEnd={(ev) => handleDragEnd(ev, productPackagings, 'packaging')}>
                    <SortableContext items={productPackagings} strategy={verticalListSortingStrategy}>
                      {productPackagings.map((attr: ProductAttribute) => (
                        <SortableItem key={attr.id} data={attr} />
                      ))}
                    </SortableContext>
                  </DndContext>
                </CustomTabPanel>
                <CustomTabPanel value={tab} index={2}>
                  <DndContext collisionDetection={closestCenter} onDragEnd={(ev) => handleDragEnd(ev, productFuntionalities, 'functionality')}>
                    <SortableContext items={productFuntionalities} strategy={verticalListSortingStrategy}>
                      {productFuntionalities.map((attr: ProductAttribute) => (
                        <SortableItem key={attr.id} data={attr} />
                      ))}
                    </SortableContext>
                  </DndContext>
                </CustomTabPanel>
                <CustomTabPanel value={tab} index={3}>
                  <DndContext collisionDetection={closestCenter} onDragEnd={(ev) => handleDragEnd(ev, productAesthetics, 'aesthetic')}>
                    <SortableContext items={productAesthetics} strategy={verticalListSortingStrategy}>
                      {productAesthetics.map((attr: ProductAttribute) => (
                        <SortableItem key={attr.id} data={attr} />
                      ))}
                    </SortableContext>
                  </DndContext>
                </CustomTabPanel>
                <CustomTabPanel value={tab} index={4}>
                  <DndContext collisionDetection={closestCenter} onDragEnd={(ev) => handleDragEnd(ev, productCompletenesses, 'completeness')}>
                    <SortableContext items={productCompletenesses} strategy={verticalListSortingStrategy}>
                      {productCompletenesses.map((attr: ProductAttribute) => (
                        <SortableItem key={attr.id} data={attr} />
                      ))}
                    </SortableContext>
                  </DndContext>
                </CustomTabPanel>
                <CustomTabPanel value={tab} index={5}>
                  <DndContext collisionDetection={closestCenter} onDragEnd={(ev) => handleDragEnd(ev, productStates, 'state')}>
                    <SortableContext items={productStates} strategy={verticalListSortingStrategy}>
                      {productStates.map((attr: ProductAttribute) => (
                        <SortableItem key={attr.id} data={attr} />
                      ))}
                    </SortableContext>
                  </DndContext>
                </CustomTabPanel>
              </div>
            </Paper>
          </div>
        }
        scroll={isMobile ? 'normal' : 'content'}
      />
      <CreateDialog type={tAttribute} cardinality={cardinality} />
      <UpdateDialog type={tAttribute} />
    </>
  );
};

export default Attributes;
